/**
 * Opens the theme's quick view content in a modal when a Boost AI Search & Filter
 * "Quick view" button is clicked, instead of Boost's own popup.
 *
 * - Intercepts the click in the capture phase (before Boost's handler).
 * - Prefetches the product page when the pointer hovers a Quick view button,
 *   so the modal opens near-instantly on click.
 * - The content wrapper carries the `js-quickbuy` class and `data-product-url`,
 *   which the theme's variant picker uses to orient itself — this makes variant
 *   changes update images, availability cross-outs, price and add-to-cart state
 *   exactly like the theme's native quick buy.
 */
(() => {
  if (window.__boostThemeQuickview) return;
  window.__boostThemeQuickview = true;

  const ROOT = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const pageCache = new Map(); // url -> Promise<string>

  function fetchPage(url) {
    if (!pageCache.has(url)) {
      const promise = fetch(url).then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.text();
      }).catch((err) => {
        pageCache.delete(url); // allow retry on failure
        throw err;
      });
      pageCache.set(url, promise);
    }
    return pageCache.get(url);
  }

  function getProductUrl(btn) {
    const card = btn.closest('[data-product]');
    if (!card) return null;
    try {
      const data = JSON.parse(card.dataset.product);
      if (!data.handle) return null;
      let url = `${ROOT}products/${data.handle}`;
      if (data.variantId) url += `?variant=${data.variantId}`;
      return url;
    } catch {
      return null;
    }
  }

  function closeModal() {
    const modal = document.querySelector('.boost-theme-qv');
    if (modal) modal.remove();
    document.body.classList.remove('boost-theme-qv-open');
  }

  function basicConvert(container, productUrl) {
    // Fallback mirroring theme.quickBuy.convertToQuickBuyContent
    container.querySelectorAll('.more').forEach((el) => { el.href = productUrl; });
    container.querySelectorAll('.show-gallery').forEach((el) => {
      el.classList.remove('show-gallery');
      el.href = productUrl;
    });
    container.querySelectorAll('.not-in-quickbuy').forEach((el) => el.remove());
    container.querySelectorAll('.only-in-quickbuy').forEach((el) => el.classList.remove('only-in-quickbuy'));
    container.querySelectorAll('.sticky-column-desktop').forEach((el) => el.classList.remove('sticky-column-desktop'));
    container.querySelectorAll('pickup-availability').forEach((el) => el.remove());
  }

  async function openModal(productUrl) {
    closeModal();

    const modal = document.createElement('div');
    modal.className = 'boost-theme-qv';
    modal.innerHTML = `
      <div class="boost-theme-qv__backdrop"></div>
      <div class="boost-theme-qv__panel" role="dialog" aria-modal="true" aria-label="Quick view">
        <button type="button" class="boost-theme-qv__close" aria-label="Close">&#10005;</button>
        <div class="boost-theme-qv__content js-quickbuy" data-product-url="${productUrl}">
          <div class="boost-theme-qv__loading">&hellip;</div>
        </div>
      </div>`;
    document.body.appendChild(modal);
    document.body.classList.add('boost-theme-qv-open');
    modal.querySelector('.boost-theme-qv__backdrop').addEventListener('click', closeModal);
    modal.querySelector('.boost-theme-qv__close').addEventListener('click', closeModal);

    try {
      const html = await fetchPage(productUrl);

      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;
      const content = tmpl.content.querySelector('.quickbuy-content');
      if (!content) throw new Error('No quickbuy content found');
      const detail = content.cloneNode(true);

      if (window.theme && theme.quickBuy && theme.quickBuy.convertToQuickBuyContent) {
        theme.quickBuy.convertToQuickBuyContent(detail, productUrl);
      } else {
        basicConvert(detail, productUrl);
      }

      // Modal content is immediately visible — don't let images wait for lazy loading
      detail.querySelectorAll('img[loading="lazy"]').forEach((img) => {
        img.loading = 'eager';
      });

      // Load any product-page stylesheets/scripts not already on this page
      tmpl.content.querySelectorAll(':is(.section-main-product, .section-size-guide) link[rel="stylesheet"]').forEach((el) => {
        if (!document.querySelector(`link[rel="stylesheet"][href="${el.getAttribute('href')}"]`)) {
          document.head.insertAdjacentHTML('beforeend', el.outerHTML);
        }
      });
      tmpl.content.querySelectorAll(':is(.section-main-product, .section-size-guide) script[src]').forEach((el) => {
        if (!document.querySelector(`script[src="${el.getAttribute('src')}"]`)) {
          const scr = document.createElement('script');
          scr.src = el.getAttribute('src');
          scr.defer = 'defer';
          document.head.appendChild(scr);
        }
      });

      const target = modal.querySelector('.boost-theme-qv__content');
      target.innerHTML = '';
      target.appendChild(detail);
    } catch (e) {
      // Anything goes wrong: fall through to the product page
      window.location.href = productUrl;
    }
  }

  // Prefetch the product page as soon as the pointer reaches a Quick view button
  document.addEventListener('pointerover', (evt) => {
    const btn = evt.target.closest('.boost-sd__button--quick-view');
    if (!btn) return;
    const productUrl = getProductUrl(btn);
    if (productUrl) fetchPage(productUrl).catch(() => {});
  }, true);

  // Capture phase so this runs before Boost's own click handler
  document.addEventListener('click', (evt) => {
    const btn = evt.target.closest('.boost-sd__button--quick-view');
    if (!btn) return;
    const productUrl = getProductUrl(btn);
    if (!productUrl) return; // no product data — let Boost handle it
    evt.preventDefault();
    evt.stopPropagation();
    if (evt.stopImmediatePropagation) evt.stopImmediatePropagation();
    openModal(productUrl);
  }, true);

  document.addEventListener('keydown', (evt) => {
    if (evt.key === 'Escape') closeModal();
  });
})();
